/**
 * Hooks for billing and subscription management
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { api, apiErrorMessage } from '../client';
import { safeExternalRedirect } from '../../lib/safeRedirect';
import { useUIStore } from '../../stores/uiStore';

// Query keys
export const billingKeys = {
  all: ['billing'] as const,
  status: () => [...billingKeys.all, 'status'] as const,
};

/**
 * Fetch current subscription status
 */
export function useSubscriptionStatus(enabled = true) {
  return useQuery({
    queryKey: billingKeys.status(),
    queryFn: () => api.billing.getStatus(),
    staleTime: 60 * 1000, // 60s
    enabled,
  });
}

/**
 * Fetch current-month account usage (analyses + chat spend) for Settings.
 * retry:false — a 401 (logged-out) should not retry.
 */
export function useAccountUsage() {
  return useQuery({
    queryKey: ['account-usage'],
    queryFn: () => api.auth.getUsage(),
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Get Stripe Customer Portal URL and redirect
 */
export function useBillingPortal() {
  return useMutation({
    mutationFn: () => api.billing.getPortalUrl(),
    onSuccess: (data) => {
      if (!safeExternalRedirect(data.portal_url)) {
        useUIStore.getState().showToast('Could not open billing. Try again.', 'error');
      }
    },
    onError: (err) => {
      useUIStore.getState().showToast(apiErrorMessage(err, 'Could not open billing. Try again.'), 'error');
    },
  });
}
